import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

// ── helpers ────────────────────────────────────────────────────────────────────

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const getYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.trim());
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1).split('/')[0];
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      if (v) return v;
      const shorts = u.pathname.match(/^\/shorts\/([^/?]+)/);
      if (shorts) return shorts[1];
    }
  } catch {}
  return null;
};

const fmt = (n) => new Intl.NumberFormat('id-ID').format(n || 0);

const SortIcon = ({ active, dir }) => (
  <svg
    className={`w-3 h-3 inline ml-1 ${active ? 'text-[#3fb5d2]' : 'opacity-40'}`}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
  >
    {active && dir === 'asc'
      ? <path d="M12 19V5M5 12l7-7 7 7"/>
      : active && dir === 'desc'
        ? <path d="M12 5v14M5 12l7 7 7-7"/>
        : <><path d="M8 9l4-4 4 4" opacity=".5"/><path d="M16 15l-4 4-4-4" opacity=".5"/></>
    }
  </svg>
);

const PlatformThumb = ({ platform }) => {
  const colors = {
    Tiktok: 'bg-black',
    Instagram: 'bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600',
    Youtube: 'bg-red-600',
  };
  return (
    <div className={`w-[56px] h-[40px] rounded-lg flex items-center justify-center ${colors[platform] || 'bg-gray-200'}`}>
      <span className="text-white text-[9px] font-bold opacity-80">{platform?.slice(0, 2)}</span>
    </div>
  );
};

const EyeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

// ── main component ─────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { profile: authProfile } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'play_count', dir: 'desc' });
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (authProfile?.role === 'hunter' || authProfile?.role === 'basic') {
      loadHunterDashboard();
    } else {
      loadAdminDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authProfile]);

  const loadHunterDashboard = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await api.get('/api/hunter/dashboard');
      setDashboardData(data);
    } catch {}
    finally { if (!silent) setLoading(false); }
  };

  const loadAdminDashboard = async () => {
    try {
      const [campaigns, proposals] = await Promise.all([
        api.get('/api/campaigns'),
        api.get('/api/proposals'),
      ]);
      setDashboardData({
        stats: {
          campaigns: campaigns.length,
          proposals: proposals.length,
          pendingProposals: proposals.filter((p) => p.status === 'pending').length,
          approvedProposals: proposals.filter((p) => p.status === 'approved').length,
        },
      });
    } catch {}
    finally { setLoading(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try { await loadHunterDashboard(true); }
    finally { setSyncing(false); }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Hunter / Basic dashboard ──────────────────────────────────────────────
  if (authProfile?.role === 'hunter' || authProfile?.role === 'basic') {
    const {
      profile,
      earnings,
      total_upload = 0,
      top_content = [],
      period_earnings,
    } = dashboardData || {};

    const sortedContent = [...top_content].sort((a, b) => {
      const mult = sortConfig.dir === 'desc' ? -1 : 1;
      if (sortConfig.key === 'social_media_posted_at') {
        return (new Date(a[sortConfig.key] || 0) - new Date(b[sortConfig.key] || 0)) * mult;
      }
      return ((a[sortConfig.key] || 0) - (b[sortConfig.key] || 0)) * mult;
    });

    const sortCols = [
      { key: 'play_count',    label: 'Views' },
      { key: 'like_count',    label: 'Likes' },
      { key: 'comment_count', label: 'Comments' },
      { key: 'share_count',   label: 'Shares' },
      { key: 'social_media_posted_at', label: 'Date Posted' },
    ];

    return (
      <div className="space-y-4">
        <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Dashboard</h1>

        {/* Row 1 — Earnings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-6" style={card}>
            <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-3">
              Total Earnings
            </p>
            <p className="text-[34px] font-bold text-[#3b3b3b] leading-none">
              {fmt(earnings?.total_earned)}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-6" style={card}>
            <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-3">
              Total Earnings This Period{' '}
              {period_earnings && (
                <span className="normal-case font-normal text-[#8a8a8a]">
                  ({period_earnings.period_start} – {period_earnings.period_end})
                </span>
              )}
            </p>
            <p className="text-[34px] font-bold text-[#3b3b3b] leading-none">
              {fmt(period_earnings?.amount)}
            </p>
          </div>
        </div>

        {/* Row 2 — View & Upload */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-5 text-center" style={card}>
            <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-2">
              Total View
            </p>
            <p className="text-[30px] font-bold text-[#3fb5d2]">
              {fmt(profile?.total_play_count)}
            </p>
          </div>
          <div className="bg-white rounded-2xl p-5 text-center" style={card}>
            <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-2">
              Total Upload
            </p>
            <p className="text-[30px] font-bold text-[#10B981]">
              {fmt(total_upload)}
            </p>
          </div>
        </div>

        {/* Top Content table */}
        <div className="bg-white rounded-2xl" style={card}>
          {/* Header */}
          <div className="flex items-start justify-between px-5 py-4 border-b border-[#e5e5e5] gap-3 flex-wrap">
            <div>
              <h2 className="text-[17px] font-bold text-[#3b3b3b]">Top Content</h2>
              <p className="text-[13px] text-[#8a8a8a] mt-0.5">Konten dengan views terbanyak</p>
            </div>
            <button
              onClick={handleSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 border border-[#3fb5d2] text-[#3fb5d2] hover:bg-[#ecf8fb] text-[13px] font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1,4 1,10 7,10"/>
                <path d="M3.51 15a9 9 0 1 0 .49-3.8"/>
              </svg>
              {syncing ? 'Syncing...' : 'Statistic Sync'}
            </button>
          </div>

          {sortedContent.length === 0 ? (
            <p className="text-center text-[#8a8a8a] text-[14px] py-10">
              Belum ada konten yang di-submit.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#e5e5e5]">
                    {[
                      { label: 'No', key: null },
                      { label: 'Video', key: null },
                      { label: 'Link', key: null },
                      { label: 'Campaign', key: null },
                      ...sortCols,
                      { label: 'Actions', key: null },
                    ].map(({ label, key }) => (
                      <th
                        key={label}
                        onClick={key ? () => handleSort(key) : undefined}
                        className={`text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 text-left whitespace-nowrap ${key ? 'cursor-pointer select-none hover:text-[#3b3b3b]' : ''}`}
                      >
                        {label}
                        {key && <SortIcon active={sortConfig.key === key} dir={sortConfig.dir} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedContent.map((item, idx) => {
                    const ytId = getYouTubeId(item.social_media_link);
                    const thumb = ytId
                      ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`
                      : null;
                    const postedAt = item.social_media_posted_at
                      ? new Date(item.social_media_posted_at).toLocaleString('id-ID', {
                          day: 'numeric', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta',
                        })
                      : '—';
                    return (
                      <tr key={item.id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 text-[13px] text-[#8a8a8a]">{idx + 1}</td>
                        <td className="px-4 py-3">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt="thumb"
                              className="w-[56px] h-[40px] object-cover rounded-lg"
                            />
                          ) : (
                            <PlatformThumb platform={item.social_media_platform} />
                          )}
                        </td>
                        <td className="px-4 py-3 max-w-[160px]">
                          <a
                            href={item.social_media_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#3fb5d2] hover:text-[#2a798c] text-[13px] block truncate transition-colors"
                            title={item.social_media_link}
                          >
                            {item.social_media_link}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[#3b3b3b] whitespace-nowrap max-w-[140px]">
                          <span className="block truncate">{item.campaign_name}</span>
                        </td>
                        {[item.play_count, item.like_count, item.comment_count, item.share_count].map((val, i) => (
                          <td key={i} className="px-4 py-3 text-[13px] text-[#3b3b3b] whitespace-nowrap">
                            {fmt(val)}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">{postedAt}</td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/proposals/${item.id}`}
                            className="text-[#8a8a8a] hover:text-[#3fb5d2] transition-colors"
                          >
                            <EyeIcon />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* News */}
        {(dashboardData?.news?.length > 0) && (
          <div className="bg-white rounded-2xl" style={card}>
            <div className="px-5 py-4 border-b border-[#e5e5e5]">
              <h2 className="text-[17px] font-bold text-[#3b3b3b]">News</h2>
              <p className="text-[13px] text-[#8a8a8a] mt-0.5">Campaign & update terbaru</p>
            </div>
            <div className="divide-y divide-[#e5e5e5]">
              {dashboardData.news.map((item, i) => (
                <div key={item.id || i} className="px-5 py-4">
                  <p className="text-[14px] font-semibold text-[#3b3b3b] mb-0.5">{item.title}</p>
                  {item.product && (
                    <p className="text-[12px] text-[#3fb5d2] font-medium mb-1">New Campaign: {item.product}</p>
                  )}
                  {item.description && (
                    <p className="text-[13px] text-[#646464] line-clamp-2">{item.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Announcements */}
        {(dashboardData?.announcements?.length > 0) && (
          <div className="bg-white rounded-2xl" style={card}>
            <div className="px-5 py-4 border-b border-[#e5e5e5]">
              <h2 className="text-[17px] font-bold text-[#3b3b3b]">Announcements</h2>
            </div>
            <div className="divide-y divide-[#e5e5e5]">
              {dashboardData.announcements.map((item, i) => (
                <div key={item.id || i} className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${item.is_pinned ? 'bg-amber-50 text-amber-600' : 'bg-[#ecf8fb] text-[#2a798c]'}`}>
                      {item.is_pinned ? (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z"/></svg>
                      ) : (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {item.is_pinned && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-600">PINNED</span>
                        )}
                        {item.title && <p className="text-[14px] font-semibold text-[#3b3b3b]">{item.title}</p>}
                      </div>
                      {item.image_url && (
                        <img src={item.image_url} alt="" className="max-h-40 rounded-lg object-cover mb-2 border border-[#e5e5e5]" />
                      )}
                      <p className="text-[13px] text-[#646464] whitespace-pre-wrap leading-5">{item.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Admin / Campaign Manager dashboard ────────────────────────────────────
  return (
    <div className="space-y-4">
      <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Dashboard</h1>
      <p className="text-[13px] text-[#8a8a8a] -mt-2">
        Selamat datang kembali, {authProfile?.full_name || authProfile?.email}!
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Total Campaigns',      value: dashboardData?.stats?.campaigns || 0,         color: 'text-[#3fb5d2]' },
          { label: 'Total Proposals',      value: dashboardData?.stats?.proposals || 0,          color: 'text-purple-600' },
          { label: 'Pending Proposals',    value: dashboardData?.stats?.pendingProposals || 0,   color: 'text-amber-600' },
          { label: 'Approved Proposals',   value: dashboardData?.stats?.approvedProposals || 0,  color: 'text-[#10B981]' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5" style={card}>
            <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-2">{label}</p>
            <p className={`text-[30px] font-bold ${color}`}>{fmt(value)}</p>
          </div>
        ))}
      </div>

      {(authProfile?.role === 'campaign_manager' || authProfile?.role === 'admin') && (
        <div className="bg-white rounded-2xl p-5" style={card}>
          <h2 className="text-[15px] font-semibold text-[#3b3b3b] mb-3">Quick Actions</h2>
          <Link
            to="/campaigns/create"
            className="inline-flex items-center gap-2 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            + Create New Campaign
          </Link>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
